import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
} from "recharts";
import { formatValue, type BaseChartConfig, type Widget } from "./types";

interface GaugeConfig extends BaseChartConfig {
  /** Current value (defaults to data.points[0].value). */
  value?: number;
  /** Max scale; defaults to 100 (pct gauge). */
  max?: number;
  /** Target threshold; when value crosses it the arc color flips. */
  target?: number;
  /** Label rendered under the big number. */
  sublabel?: string;
  /** Smaller secondary stat (e.g. "vs. 30% target"). */
  caption?: string;
  /** Inverted threshold: when true, value < target is "good" (e.g. labor%, cost%). */
  lower_is_better?: boolean;
}

export function GaugeCard({ widget }: { widget: Widget }) {
  const cfg = widget.config as GaugeConfig;
  const theme = useTheme();

  // Resolve value from explicit config OR first data point.
  let value = cfg.value;
  if (value === undefined && widget.data?.points?.length) {
    const first = widget.data.points[0] as Record<string, unknown>;
    const num = Object.entries(first).find(
      ([k, v]) => k !== "label" && typeof v === "number",
    );
    if (num) value = num[1] as number;
  }
  const max = cfg.max ?? 100;
  const v = value ?? 0;
  const pct = max ? Math.min(100, Math.max(0, (v / max) * 100)) : 0;

  // Color: green = on target, red = over (or under if lower_is_better=false)
  const onTarget =
    cfg.target === undefined
      ? true
      : cfg.lower_is_better
        ? v <= cfg.target
        : v >= cfg.target;
  const fill =
    cfg.color ?? (onTarget ? theme.palette.success.main : theme.palette.error.main);
  const track = theme.palette.divider;

  const data = [{ name: "value", value: pct, fill }];

  return (
    <Card variant="outlined">
      <CardContent>
        {widget.title && (
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.08em", fontWeight: 600 }}
          >
            {widget.title}
          </Typography>
        )}
        <Box sx={{ position: "relative", width: "100%", height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              cx="50%"
              cy="55%"
              innerRadius="70%"
              outerRadius="100%"
              barSize={16}
              data={data}
              startAngle={210}
              endAngle={-30}
            >
              <PolarAngleAxis
                type="number"
                domain={[0, 100]}
                tick={false}
              />
              <RadialBar background={{ fill: track }} dataKey="value" cornerRadius={8} />
            </RadialBarChart>
          </ResponsiveContainer>
          {/* Centered value overlay */}
          <Stack
            spacing={0.25}
            alignItems="center"
            sx={{
              position: "absolute",
              top: "55%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {formatValue(v, cfg.format ?? "number")}
            </Typography>
            {cfg.sublabel && (
              <Typography variant="caption" color="text.secondary">
                {cfg.sublabel}
              </Typography>
            )}
          </Stack>
        </Box>
        {cfg.caption && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 1 }}
          >
            {cfg.caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
